// /admin/orgs/:id — platform-admin detail panel for one org.
//
// Per ADR-0033 §Decision 3, this view composes two parallel queries:
//   - core-api  GET /admin/orgs/:id           → recipe, brands, counts, config
//   - auth-svc  GET /admin/orgs/:id/members   → user roster
//
// Panels: Identificación, Logo de la organización, Logos por marca,
// Configuración, Usuarios. The org logo (ADR-0164) and every brand logo
// share one three-step upload flow (presign → PUT to R2 → commit), which
// lives in ./logoUpload.ts; each row is a LogoUploadRow.
//
// This is an internal platform-admin surface — visually utilitarian on
// purpose. Use existing dashboard.css classes; small amount of inline
// style for layout where adding a class wouldn't pay for itself.

import { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isAssetScopedInviteRole, isBrandScopedRole, type Role } from "@bitacorapro/api-types";
import { ApiError } from "../../lib/api/error";
import { useQueryView, type QueryView } from "../../lib/loading/useQueryView";
import { PageError } from "../../components/PageError";
import { PageSkeleton } from "../../components/PageSkeleton";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skel } from "../../components/Skel";
import { SkelPanel } from "../../components/skeleton/SkelPanel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import {
  invalidateAfterLogo,
  useAdminOrg,
  useAdminOrgAssets,
  useAdminOrgMembers,
  useCreateBrand,
  useCreateInvitation,
  useDeleteAdminOrg,
  usePatchAdminOrg,
  useRetryFailedInvitations,
  useSetDefaultBrand,
} from "./queries";
import type { LOGO_ALLOWED_MIME } from "./logoUpload";
import {
  LOGO_INVALID_FILE_MESSAGE,
  LOGO_PUT_FAILED_MESSAGE,
  LOGO_SESSION_EXPIRED_MESSAGE,
  uploadLogoFile,
  validateLogoFile,
  type LogoTarget,
} from "./logoUpload";
import { adminApi } from "../../lib/api/endpoints/admin";
import { InviteBrandField } from "../../components/InviteBrandField";
import { LogoUploadRow, type LogoUploadStatus } from "../../components/LogoUploadRow";
import { StorePicker } from "../../components/StorePicker";

// Roles offered in the invitation form. The server-side roleSchema
// accepts six total; this panel offers the five a platform admin
// plausibly seeds by hand for facility_management orgs — admin (full org
// control), company_manager ("Gerente", org-scoped), brand_manager
// ("Gerente de marca", brand-scoped and read-only, ADR-0164 — always to a
// specific email, always with a brand), store_manager ("Jefe de tienda",
// asset-scoped) and facility_supervisor ("Supervisor", asset-scoped).
// inspector doesn't come from this panel, and brand_manager doesn't come
// from the workbook ingest either — the workbook only ever produces
// company_manager (Managers-sheet rows — ADR-0119; this used to say
// "admin", which was the bug ADR-0119 fixed), store_manager (jefe), and
// facility_supervisor (supervisor); see collectPendingInvitations in
// workbook-writer.ts.
//
// Known gap, deliberately not fully fixed here (docs/decisions/0091-
// facility-invite-role-widening-known-gap.md): this static list is
// filtered against the target org's recipe below
// (targetMemberRoleKeys/availableInvitableRoles), but the server does
// not validate role membership against the recipe either yet. For
// company_manager specifically, an org whose recipe doesn't declare it
// can resolve the invitee to org-wide read scope (ORG_SCOPED_ROLES
// fallback in scoped-assets.ts). Safe as long as this panel is only used
// to invite facility roles into facility_management orgs, which is its
// only real usage today.
const INVITABLE_ROLES: { value: Role; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "company_manager", label: "Gerente" },
  { value: "brand_manager", label: "Gerente de marca" },
  { value: "store_manager", label: "Jefe de tienda" },
  { value: "facility_supervisor", label: "Supervisor" },
];

const MEMBER_COLUMNS = ["Email", "Nombre", "Rol", "Estado", "Alta"];
// The Identificación panel's labels are static copy: the skeleton
// renders them for real and bars only the values, so the panel lands
// with no shift.
const IDENT_LABELS = ["ID", "Recipe", "RUT", "Estado", "Creada", "Zonas / Tiendas", "Reportes"];

const BRAND_REQUIRED_MESSAGE = "Elige la marca que va a gestionar esta persona.";
const ORG_LOGO_KEY = "org";

// The brand's horizontal logo slot (canal único fase 1) rides the same
// upload state machine as the square one, under its own row key.
type LogoPickTarget = LogoTarget | { kind: "brandWide"; brandId: string };
function wideLogoKey(brandId: string): string {
  return `${brandId}-wide`;
}

// Horizontal brand logo (canal único fase 1): same presign → PUT → commit
// wire as uploadLogoFile, but never padded — it is committed with
// kind "wide" into brands.config.logoWideUrl. A reasonable landscape
// aspect is validated instead: the owner header renders it at 30 px
// tall, so a near-square or extreme-banner image would read as a smudge
// there. Validation is client-side on purpose: core-api/media-svc never
// decode image bytes (same stance that puts padToSquare in the client).
async function uploadWideBrandLogo(orgId: string, brandId: string, file: File): Promise<void> {
  const invalid = validateLogoFile(file);
  if (invalid) throw new Error(invalid);
  // validateLogoFile already admitted the type; narrow it for the presign.
  const contentType = file.type as (typeof LOGO_ALLOWED_MIME)[number];

  let ratio: number;
  try {
    const bitmap = await createImageBitmap(file);
    ratio = bitmap.width / bitmap.height;
    bitmap.close();
  } catch {
    throw new Error(LOGO_INVALID_FILE_MESSAGE);
  }
  if (!(ratio >= 1.5 && ratio <= 8)) {
    const ratioLabel = ratio.toLocaleString("es-CL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    throw new Error(
      `El logo horizontal debe ser apaisado: entre 1,5:1 y 8:1 (este archivo es ${ratioLabel}:1). Para el isotipo cuadrado usa «Subir logo».`
    );
  }

  try {
    const presign = await adminApi.requestBrandLogoUploadUrl(orgId, brandId, {
      contentType,
      contentLength: file.size,
    });
    let putOk = false;
    try {
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: file,
        headers: presign.requiredHeaders,
      });
      putOk = putRes.ok;
    } catch {
      putOk = false;
    }
    if (!putOk) throw new Error(LOGO_PUT_FAILED_MESSAGE);
    await adminApi.commitBrandLogo(orgId, brandId, {
      objectKey: presign.objectKey,
      kind: "wide",
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      throw new Error(LOGO_SESSION_EXPIRED_MESSAGE);
    }
    throw err;
  }
}

export function AdminOrgDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const orgQuery = useAdminOrg(id);
  const orgView = useQueryView(orgQuery);
  const membersQuery = useAdminOrgMembers(id);
  const setDefaultBrand = useSetDefaultBrand(id);
  const createBrand = useCreateBrand(id);
  const createInvitation = useCreateInvitation(id);
  const deleteOrg = useDeleteAdminOrg(id);
  const patchOrg = usePatchAdminOrg(id);
  const retryInvitations = useRetryFailedInvitations(id);

  // Dangerous-action panel state. `dangerOpen` toggles the destructive
  // section's body; `dangerTyped` holds the operator's typed-name
  // confirmation; the delete button enables only when typed equals org
  // name verbatim. Error surfaces via the mutation's error state.
  const [dangerOpen, setDangerOpen] = useState(false);
  const [dangerTyped, setDangerTyped] = useState("");

  // Single inline state machine for the logo uploads. Only one upload
  // runs at a time; the row key ("org", a brandId, or `${brandId}-wide`
  // for the brand's horizontal slot) discriminates which row shows
  // progress / errors. The last picked file per row is kept so
  // «Reintentar» doesn't reopen the picker.
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [lastDoneKey, setLastDoneKey] = useState<string | null>(null);
  const [uploadErrorByKey, setUploadErrorByKey] = useState<Record<string, string>>({});
  const lastFileByKey = useRef<Record<string, File>>({});
  const [defaultBrandError, setDefaultBrandError] = useState<string | null>(null);

  // Inline form state — kept here rather than extracted because each
  // form is ~5 inputs total and the panel is internal use.
  const [newBrandName, setNewBrandName] = useState("");
  const [brandFormError, setBrandFormError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("admin");
  const [inviteFormError, setInviteFormError] = useState<string | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  // brand_manager invitations (ADR-0164) carry the brand it will manage.
  // Never preselected; cleared on every role change.
  const [inviteBrandId, setInviteBrandId] = useState("");
  const [inviteBrandError, setInviteBrandError] = useState<string | null>(null);
  const brandSelectRef = useRef<HTMLSelectElement | null>(null);
  // Multi-store picker (ADR-0047, widened to store_manager). Selection is
  // only meaningful for the two asset-scoped roles; it is cleared only
  // when crossing the org-scoped/asset-scoped boundary in either
  // direction (not on every role change), so switching between
  // store_manager and facility_supervisor keeps the operator's picks.
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [lastInviteStoreCount, setLastInviteStoreCount] = useState(0);

  // Filters the static INVITABLE_ROLES list against the TARGET org's
  // own recipe-declared roles (already on hand via
  // orgQuery.data.configuration — no extra request). Purely a frontend
  // narrowing of an existing deliberate subset; INVITABLE_ROLES itself
  // stays unchanged, and this does not widen or replace it. Computed
  // here (ahead of the loading/error early returns below) via optional
  // chaining on `orgQuery.data`, not the later `org`/`config` consts,
  // because `assetScopeSelected`/`useAdminOrgAssets` are hook-adjacent
  // and must be called on every render regardless of query state.
  const targetMemberRoleKeys = useMemo(
    () => new Set(orgQuery.data?.configuration?.memberRoles.map((r) => r.key) ?? []),
    [orgQuery.data?.configuration]
  );
  const availableInvitableRoles = useMemo(
    () => INVITABLE_ROLES.filter((r) => targetMemberRoleKeys.has(r.value)),
    [targetMemberRoleKeys]
  );
  // Re-derives every render off availableInvitableRoles — same
  // auto-reclamp posture as Equipo.tsx's effectiveInviteRole.
  const effectiveInviteRole = availableInvitableRoles.some((r) => r.value === inviteRole)
    ? inviteRole
    : (availableInvitableRoles[0]?.value ?? inviteRole);

  const assetScopeSelected = isAssetScopedInviteRole(effectiveInviteRole);
  const brandScopeSelected = isBrandScopedRole(effectiveInviteRole);
  const assetsQuery = useAdminOrgAssets(id, assetScopeSelected);
  const orgHasNoStores =
    assetScopeSelected && assetsQuery.isSuccess && (assetsQuery.data?.assets.length ?? 0) === 0;

  if (orgView.kind !== "ready") {
    return <AdminOrgDetailFallback view={orgView} />;
  }

  const org = orgView.data;
  const config = org.configuration;
  const findingCategories = config?.findingCategories ?? [];

  async function handleCreateBrand(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setBrandFormError(null);
    const trimmed = newBrandName.trim();
    if (trimmed.length === 0) {
      setBrandFormError("El nombre no puede estar vacío.");
      return;
    }
    try {
      await createBrand.mutateAsync({ name: trimmed });
      setNewBrandName("");
    } catch (err) {
      setBrandFormError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err)
      );
    }
  }

  async function handleCreateInvitation(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setInviteFormError(null);
    setLastInviteUrl(null);
    const trimmed = inviteEmail.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      setInviteFormError("Ingresa un email válido.");
      return;
    }
    // A store_manager or facility_supervisor without stores would land
    // org-scoped (visibility into every tienda) — almost never the
    // intent. Hard-block at submit.
    if (assetScopeSelected && selectedAssetIds.length === 0) {
      setInviteFormError(
        "Selecciona al menos una tienda antes de invitar con este rol. Los roles de tienda siempre tienen asignación a tiendas específicas."
      );
      return;
    }
    if (brandScopeSelected && inviteBrandId === "") {
      setInviteBrandError(BRAND_REQUIRED_MESSAGE);
      brandSelectRef.current?.focus();
      return;
    }
    setInviteBrandError(null);
    try {
      const res = await createInvitation.mutateAsync({
        email: trimmed,
        role: effectiveInviteRole,
        ...(assetScopeSelected && selectedAssetIds.length > 0
          ? { assetIds: selectedAssetIds }
          : {}),
        ...(brandScopeSelected ? { scopeBrandId: inviteBrandId } : {}),
      });
      setLastInviteStoreCount(assetScopeSelected ? selectedAssetIds.length : 0);
      setInviteEmail("");
      setSelectedAssetIds([]);
      setInviteBrandId("");
      setLastInviteUrl(res.acceptUrl);
    } catch (err) {
      setInviteFormError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err)
      );
    }
  }

  async function handleLogoPick(key: string, target: LogoPickTarget, file: File): Promise<void> {
    lastFileByKey.current[key] = file;
    setUploadingKey(key);
    setLastDoneKey(null);
    setUploadErrorByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      if (target.kind === "brandWide") {
        await uploadWideBrandLogo(id, target.brandId, file);
      } else {
        await uploadLogoFile(id, target, file);
      }
      await invalidateAfterLogo(qc, id);
      setLastDoneKey(key);
    } catch (err) {
      setUploadErrorByKey((prev) => ({
        ...prev,
        [key]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setUploadingKey(null);
    }
  }

  function logoRowStatus(key: string): LogoUploadStatus | undefined {
    if (uploadingKey === key) return { kind: "uploading" };
    if (uploadErrorByKey[key]) return { kind: "error" };
    if (lastDoneKey === key) return { kind: "done" };
    return undefined;
  }

  function retryFor(key: string, target: LogoPickTarget): (() => void) | undefined {
    const file = lastFileByKey.current[key];
    if (!file) return undefined;
    return () => void handleLogoPick(key, target, file);
  }

  async function handleSetDefaultBrand(brandId: string): Promise<void> {
    setDefaultBrandError(null);
    try {
      await setDefaultBrand.mutateAsync({ brandId });
    } catch (err) {
      setDefaultBrandError(
        err instanceof ApiError
          ? err.message
          : "No pudimos cambiar la marca principal. Inténtalo de nuevo."
      );
    }
  }

  async function handleDeleteConfirm(): Promise<void> {
    if (dangerTyped !== org.name) return;
    try {
      await deleteOrg.mutateAsync({ confirmationName: dangerTyped });
      navigate("/admin/orgs");
    } catch {
      // mutation state surfaces the error below
    }
  }

  return (
    <div className="page">
      <div className="page-pad">
        <header className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/admin/orgs">← Organizaciones</Link>
            </div>
            <h1 className="page-header__title">{org.name}</h1>
            <p className="page-header__sub">
              Recipe <code>{org.recipeSlug}</code>
              {org.rut ? ` · RUT ${org.rut}` : ""} · {org.active ? "activa" : "inactiva"}
            </p>
          </div>
        </header>

        {orgView.refreshFailed && (
          <div className="notice-banner notice-banner--action">
            <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => void orgQuery.refetch()}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* ── Panel 1 — Identificación ─────────────────────────── */}
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Identificación</h2>
          </div>
          <div className="panel__body">
            <dl className="kv-grid">
              <dt className="cell-meta">ID</dt>
              <dd>
                <code>{org.id}</code>
              </dd>
              <dt className="cell-meta">Recipe</dt>
              <dd>
                <code>{org.recipeSlug}</code>
              </dd>
              <dt className="cell-meta">RUT</dt>
              <dd>{org.rut ?? "—"}</dd>
              <dt className="cell-meta">Estado</dt>
              <dd style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {org.active ? (
                  <span className="st-pill st-pill--ok">Activa</span>
                ) : (
                  <span className="st-pill st-pill--idle">Inactiva</span>
                )}
                <button
                  type="button"
                  className="btn btn--secondary"
                  style={{ padding: "4px 10px", fontSize: "var(--t-12)" }}
                  onClick={() => {
                    const willDisable = org.active;
                    if (willDisable) {
                      const ok = window.confirm(
                        `Desactivar "${org.name}"? La org desaparecerá del listado y los logins quedarán bloqueados. La data se preserva — puedes reactivarla cuando quieras.`
                      );
                      if (!ok) return;
                    }
                    patchOrg.mutate({ active: !org.active });
                  }}
                  disabled={patchOrg.isPending}
                >
                  {patchOrg.isPending ? "Actualizando…" : org.active ? "Desactivar" : "Reactivar"}
                </button>
                {patchOrg.error && (
                  <span className="auth-error" role="alert" style={{ fontSize: "var(--t-12)" }}>
                    {patchOrg.error instanceof ApiError ? patchOrg.error.message : "Falló."}
                  </span>
                )}
              </dd>
              <dt className="cell-meta">Creada</dt>
              <dd>{new Date(org.createdAt).toLocaleString("es-CL")}</dd>
              <dt className="cell-meta">Zonas / Tiendas</dt>
              <dd>
                {org.counts.assetGroups} grupo
                {org.counts.assetGroups === 1 ? "" : "s"} · {org.counts.assets} asset
                {org.counts.assets === 1 ? "" : "s"}
              </dd>
              <dt className="cell-meta">Reportes</dt>
              <dd>
                <Link to={`/admin/orgs/${id}/reports`}>
                  Ver reportes ({org.counts.reports} total · {org.counts.signedReports} firmado
                  {org.counts.signedReports === 1 ? "" : "s"} o cerrado
                  {org.counts.signedReports === 1 ? "" : "s"})
                </Link>
              </dd>
            </dl>
          </div>
        </section>

        {/* ── Panel 2 — Logo de la organización (ADR-0164) ───── */}
        <section className="panel" aria-labelledby="org-logo-heading">
          <div className="panel__head">
            <h2 id="org-logo-heading" className="panel__title">
              Logo de la organización
            </h2>
            <p className="panel__sub">
              Logo corporativo. Lo ven en el menú lateral quienes gestionan toda la organización, y
              los jefes de tienda y supervisores con tiendas de varias marcas. Cada marca tiene su
              propio logo más abajo.
            </p>
          </div>
          <div className="panel__body stack-gap stack-gap--sm">
            <ul className="brand-list">
              <LogoUploadRow
                itemKey={ORG_LOGO_KEY}
                title={org.name}
                pill={<span className="st-pill st-pill--brand">Corporativo</span>}
                previewUrl={org.logoUrl ?? null}
                status={logoRowStatus(ORG_LOGO_KEY)}
                primaryLabel={org.logoUrl ? "Reemplazar logo" : "Subir logo"}
                onPick={(file) => void handleLogoPick(ORG_LOGO_KEY, { kind: "org" }, file)}
                onRetry={retryFor(ORG_LOGO_KEY, { kind: "org" })}
                disabled={uploadingKey !== null && uploadingKey !== ORG_LOGO_KEY}
              />
            </ul>
            {!org.logoUrl && (
              <p className="cell-meta">
                Aún no hay logo de la organización. Mientras tanto, el menú lateral muestra el logo
                de la marca principal o las iniciales.
              </p>
            )}
            <div role="status" className="cell-meta">
              {lastDoneKey === ORG_LOGO_KEY ? "Logo actualizado." : null}
            </div>
            {uploadErrorByKey[ORG_LOGO_KEY] && (
              <p className="notice-banner" role="alert">
                {uploadErrorByKey[ORG_LOGO_KEY]}
              </p>
            )}
          </div>
        </section>

        {/* ── Panel 3 — Logos por marca ────────────────────────── */}
        <section className="panel" aria-labelledby="brand-logos-heading">
          <div className="panel__head">
            <h2 id="brand-logos-heading" className="panel__title">
              Logos por marca
            </h2>
            <p className="panel__sub">
              Cada marca tiene su propio logo. Lo ven en el menú lateral el gerente de esa marca y
              quienes solo tienen tiendas de esa marca.
            </p>
          </div>
          <div className="panel__body">
            {org.brands.length === 0 ? (
              <p className="cell-meta">
                Sin marcas registradas. Ejecuta <code>pnpm db:seed:default-brands</code> para crear
                una por defecto.
              </p>
            ) : (
              <div className="stack-gap stack-gap--sm">
                <ul className="brand-list">
                  {org.brands.map((brand) => {
                    const target: LogoTarget = { kind: "brand", brandId: brand.id };
                    const defaultBtnId = `logo-default-${brand.id}`;
                    const wideKey = wideLogoKey(brand.id);
                    const wideTarget: LogoPickTarget = { kind: "brandWide", brandId: brand.id };
                    return [
                      <LogoUploadRow
                        key={brand.id}
                        itemKey={brand.id}
                        title={brand.name}
                        pill={
                          brand.isDefault ? (
                            <span className="st-pill st-pill--brand">Marca principal</span>
                          ) : undefined
                        }
                        previewUrl={brand.logoUrl}
                        status={logoRowStatus(brand.id)}
                        primaryLabel={brand.logoUrl ? "Reemplazar logo" : "Subir logo"}
                        onPick={(file) => void handleLogoPick(brand.id, target, file)}
                        onRetry={retryFor(brand.id, target)}
                        disabled={uploadingKey !== null && uploadingKey !== brand.id}
                        extraActions={
                          brand.isDefault ? undefined : (
                            <button
                              id={defaultBtnId}
                              type="button"
                              className="btn btn--secondary btn--sm"
                              aria-labelledby={`${defaultBtnId} logo-name-${brand.id}`}
                              disabled={setDefaultBrand.isPending}
                              onClick={() => void handleSetDefaultBrand(brand.id)}
                            >
                              Marcar como principal
                            </button>
                          )
                        }
                      />,
                      // Horizontal slot (canal único fase 1) — the logo the
                      // owner-home header shows. Never padded; the header
                      // falls back to the isotipo while it is empty.
                      <LogoUploadRow
                        key={wideKey}
                        itemKey={wideKey}
                        title={`${brand.name} · logo horizontal`}
                        previewUrl={brand.logoWideUrl ?? null}
                        status={logoRowStatus(wideKey)}
                        primaryLabel={
                          brand.logoWideUrl ? "Reemplazar logo horizontal" : "Subir logo horizontal"
                        }
                        onPick={(file) => void handleLogoPick(wideKey, wideTarget, file)}
                        onRetry={retryFor(wideKey, wideTarget)}
                        disabled={uploadingKey !== null && uploadingKey !== wideKey}
                      />,
                    ];
                  })}
                </ul>
                <div role="status" className="cell-meta">
                  {lastDoneKey !== null && lastDoneKey !== ORG_LOGO_KEY
                    ? "Logo actualizado."
                    : null}
                </div>
                {org.brands.map((brand) =>
                  uploadErrorByKey[brand.id] ? (
                    <p key={brand.id} className="notice-banner" role="alert">
                      {brand.name}: {uploadErrorByKey[brand.id]}
                    </p>
                  ) : null
                )}
                {org.brands.map((brand) =>
                  uploadErrorByKey[wideLogoKey(brand.id)] ? (
                    <p key={wideLogoKey(brand.id)} className="notice-banner" role="alert">
                      {brand.name} (logo horizontal): {uploadErrorByKey[wideLogoKey(brand.id)]}
                    </p>
                  ) : null
                )}
                {defaultBrandError && (
                  <p className="auth-error" role="alert">
                    {defaultBrandError}
                  </p>
                )}
              </div>
            )}
            <p className="cell-meta" style={{ marginTop: 12 }}>
              PNG, JPG o WebP · máx. 2 MB. El logo horizontal (apaisado, entre 1,5:1 y 8:1) es el
              que ve el propietario en el encabezado de su app; el cuadrado es el isotipo.
            </p>

            <form
              onSubmit={(e) => void handleCreateBrand(e)}
              className="inline-form"
              style={{ marginTop: 16 }}
            >
              <label
                htmlFor="new-brand-name"
                className="cell-meta"
                style={{ display: "block", marginBottom: 4 }}
              >
                Agregar marca
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  id="new-brand-name"
                  type="text"
                  className="input"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  placeholder="Nombre de la marca"
                  maxLength={120}
                  disabled={createBrand.isPending}
                  style={{ flex: 1 }}
                />
                <button
                  type="submit"
                  className="btn btn--primary btn--sm"
                  disabled={createBrand.isPending}
                >
                  {createBrand.isPending ? "Creando…" : "Crear"}
                </button>
              </div>
              {brandFormError && (
                <div className="auth-error" role="alert" style={{ marginTop: 8 }}>
                  {brandFormError}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* ── Panel 4 — Configuración ──────────────────────────── */}
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Configuración</h2>
          </div>
          <div className="panel__body">
            {org.configurationError ? (
              <div className="auth-error" role="alert">
                Error al parsear <code>organizations.config</code>: {org.configurationError.message}
              </div>
            ) : !config ? (
              <p className="cell-meta">Sin configuración disponible.</p>
            ) : (
              <>
                <div className="kv-grid" style={{ marginBottom: 16 }}>
                  <span className="cell-meta">Vertical</span>
                  <span>{config.labels.verticalName}</span>
                  <span className="cell-meta">Organización</span>
                  <span>{config.labels.organization}</span>
                  <span className="cell-meta">Asset</span>
                  <span>
                    {config.labels.asset} ({config.labels.assetPlural})
                  </span>
                  <span className="cell-meta">Grupo</span>
                  <span>
                    {config.labels.assetGroup} ({config.labels.assetGroupPlural})
                  </span>
                  <span className="cell-meta">Reporte</span>
                  <span>{config.labels.report}</span>
                  <span className="cell-meta">Hallazgo</span>
                  <span>
                    {config.labels.finding} ({config.labels.findingPlural})
                  </span>
                  <span className="cell-meta">Umbral "al día"</span>
                  <span>{config.alDiaThresholdDays} días</span>
                </div>

                <h3
                  style={{
                    margin: "8px 0 8px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--ink-2)",
                  }}
                >
                  Categorías de hallazgos ({findingCategories.length})
                </h3>
                {findingCategories.length === 0 ? (
                  <p className="cell-meta">Sin categorías configuradas.</p>
                ) : (
                  <ul className="config-list">
                    {findingCategories.map((cat) => (
                      <li key={cat.key} className="config-list__row">
                        <span className="cell-strong">{cat.label}</span>
                        <span className="cell-meta">
                          <code>{cat.key}</code>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {config.severities.length > 0 && (
                  <>
                    <h3
                      style={{
                        margin: "16px 0 8px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--ink-2)",
                      }}
                    >
                      Severidades ({config.severities.length})
                    </h3>
                    <ul className="config-list">
                      {config.severities.map((sev) => (
                        <li key={sev.key} className="config-list__row">
                          <span className="cell-strong">{sev.label}</span>
                          <span className="cell-meta">
                            <code>{sev.key}</code> · rank {sev.rank}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Panel 5 — Usuarios ───────────────────────────────── */}
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Usuarios</h2>
          </div>
          <div className="panel__body">
            <QueryBoundary
              query={membersQuery}
              label="los miembros"
              kind="section"
              skeleton={
                <SkelTable rows={3} cols={MEMBER_COLUMNS.length} headers={MEMBER_COLUMNS} compact />
              }
              isEmpty={(d) => d.members.length === 0}
              empty={
                <p className="cell-meta">
                  Sin miembros. Usa <Link to="/admin/carga-masiva">carga masiva</Link> o el flujo de
                  invitaciones para sumar el primero.
                </p>
              }
            >
              {(data) => (
                <table className="dtable dtable--compact">
                  <thead>
                    <tr>
                      {MEMBER_COLUMNS.map((c) => (
                        <th key={c} scope="col">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((m) => (
                      <tr key={m.userId}>
                        <td>
                          <code>{m.email}</code>
                        </td>
                        <td>{m.displayName}</td>
                        <td>
                          <span className="role-pill">{m.role}</span>
                        </td>
                        <td>
                          {m.status === "active" ? (
                            <span className="st-pill st-pill--ok">activo</span>
                          ) : (
                            <span className="st-pill st-pill--idle">desactivado</span>
                          )}
                        </td>
                        <td>
                          <span className="cell-meta">
                            {new Date(m.joinedAt).toLocaleDateString("es-CL")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </QueryBoundary>
            <form
              onSubmit={(e) => void handleCreateInvitation(e)}
              noValidate
              className="inline-form"
              style={{ marginTop: 16 }}
            >
              <h3
                className="cell-meta"
                style={{ marginTop: 0, marginBottom: 4, fontWeight: "inherit" }}
              >
                Invitar miembro
              </h3>
              {org.configurationError ? (
                <div className="auth-error" role="alert">
                  No pudimos cargar la configuración: {org.configurationError.message}
                </div>
              ) : !config ? (
                <p className="cell-meta">Sin configuración disponible.</p>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      id="invite-email"
                      type="email"
                      className="input"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="correo@dominio.cl"
                      maxLength={254}
                      disabled={createInvitation.isPending}
                      required
                      style={{ flex: "1 1 240px" }}
                    />
                    <select
                      className="input"
                      value={effectiveInviteRole}
                      onChange={(e) => {
                        const next = e.target.value as Role;
                        // Clear picked stores only when crossing the
                        // org-scoped/asset-scoped boundary, in either
                        // direction — switching between the two asset-scoped
                        // roles (store_manager ↔ facility_supervisor) keeps
                        // the selection instead of silently dropping it.
                        if (
                          isAssetScopedInviteRole(next) !==
                          isAssetScopedInviteRole(effectiveInviteRole)
                        ) {
                          setSelectedAssetIds([]);
                        }
                        setInviteBrandId("");
                        setInviteBrandError(null);
                        setInviteRole(next);
                      }}
                      disabled={createInvitation.isPending}
                      aria-label="Rol del invitado"
                      style={{ flex: "0 0 auto", minWidth: 160 }}
                    >
                      {availableInvitableRoles.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={
                        createInvitation.isPending ||
                        orgHasNoStores ||
                        (brandScopeSelected && org.brands.length === 0)
                      }
                      title={
                        orgHasNoStores ? "Agrega tiendas antes de invitar con este rol" : undefined
                      }
                    >
                      {createInvitation.isPending ? "Enviando…" : "Invitar"}
                    </button>
                  </div>
                  {brandScopeSelected && (
                    <InviteBrandField
                      ref={brandSelectRef}
                      brands={org.brands.map((b) => ({ id: b.id, name: b.name }))}
                      value={inviteBrandId}
                      onChange={(v) => {
                        setInviteBrandId(v);
                        if (v !== "") setInviteBrandError(null);
                      }}
                      error={inviteBrandError}
                      idPrefix="admin-invite"
                    />
                  )}
                  {assetScopeSelected && (
                    <div style={{ marginTop: 12 }}>
                      <QueryBoundary
                        query={assetsQuery}
                        label="las tiendas"
                        kind="section"
                        skeleton={<StorePickerSkeleton />}
                        isEmpty={(d) => d.assets.length === 0}
                        empty={
                          <div className="store-picker store-picker--empty">
                            <div className="store-picker__legend">
                              Esta organización aún no tiene tiendas.
                            </div>
                            <p className="cell-meta" style={{ margin: 0 }}>
                              Los roles de tienda se asignan a tiendas específicas. Carga las
                              tiendas primero (carga masiva o panel de tiendas) y luego invita con
                              este rol.
                            </p>
                          </div>
                        }
                      >
                        {(data) => (
                          <StorePicker
                            stores={data.assets}
                            selectedIds={selectedAssetIds}
                            onChange={setSelectedAssetIds}
                            disabled={createInvitation.isPending}
                          />
                        )}
                      </QueryBoundary>
                    </div>
                  )}
                </>
              )}
              {inviteFormError && (
                <div className="auth-error" role="alert" style={{ marginTop: 8 }}>
                  {inviteFormError}
                </div>
              )}
              {lastInviteUrl && (
                <div className="cell-meta" role="status" style={{ marginTop: 8 }}>
                  Invitación enviada.{" "}
                  {lastInviteStoreCount > 0 &&
                    `Se asign${lastInviteStoreCount === 1 ? "ó" : "aron"} ${lastInviteStoreCount} tienda${lastInviteStoreCount === 1 ? "" : "s"} al aceptar. `}
                  Link de acceso (también enviado por correo):{" "}
                  <code
                    style={{
                      wordBreak: "break-all",
                      display: "inline-block",
                      maxWidth: "100%",
                    }}
                  >
                    {lastInviteUrl}
                  </code>
                </div>
              )}
            </form>

            <p className="cell-meta" style={{ marginTop: 12 }}>
              Gestión de roles, remociones y desactivaciones desde el panel Equipo de la org (v1.5).
              Para reactivar / desactivar miembros hoy: <code>PATCH /auth/memberships/:id</code>.
            </p>

            <div
              style={{
                marginTop: 16,
                padding: "12px 14px",
                background: "var(--paper-2)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => retryInvitations.mutate()}
                  disabled={retryInvitations.isPending}
                >
                  {retryInvitations.isPending ? "Reenviando…" : "Reenviar invitaciones faltantes"}
                </button>
                <span className="cell-meta" style={{ fontSize: "var(--t-12)" }}>
                  Re-envía a jefes y supervisores cuyos emails están en <code>assets</code> pero no
                  en <code>auth_invitations</code>. No incluye filas Managers — para esas usa el
                  formulario de arriba.
                </span>
              </div>
              {retryInvitations.data && (
                <div style={{ marginTop: 10, fontSize: "var(--t-13)" }} role="status">
                  {retryInvitations.data.attempted === 0 ? (
                    <span>No hay invitaciones faltantes — todas las filas tienen invitación.</span>
                  ) : (
                    <>
                      <strong>{retryInvitations.data.succeeded}</strong> de{" "}
                      <strong>{retryInvitations.data.attempted}</strong> enviadas.
                      {retryInvitations.data.attempted - retryInvitations.data.succeeded > 0 && (
                        <details style={{ marginTop: 6 }}>
                          <summary>
                            {retryInvitations.data.attempted - retryInvitations.data.succeeded}{" "}
                            fallidas
                          </summary>
                          <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                            {retryInvitations.data.results
                              .filter((r) => !r.ok)
                              .map((r) => (
                                <li key={r.email}>
                                  <code>{r.email}</code> ({r.role}):{" "}
                                  {r.error ?? "razón desconocida"}
                                </li>
                              ))}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}
              {retryInvitations.error && (
                <div className="auth-error" role="alert" style={{ marginTop: 10 }}>
                  {retryInvitations.error instanceof ApiError
                    ? retryInvitations.error.message
                    : "Falló el reintento."}
                </div>
              )}
            </div>
          </div>
        </section>

        {orgQuery.data && (
          <section className="panel" style={{ borderColor: "var(--danger-line)" }}>
            <div className="panel__head">
              <h2 className="panel__title" style={{ color: "var(--danger-fg)" }}>
                Zona peligrosa
              </h2>
              <p className="panel__sub">Acciones irreversibles sobre esta organización.</p>
            </div>
            <div style={{ padding: "16px 18px" }}>
              {!dangerOpen ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setDangerOpen(true)}
                  >
                    Eliminar organización…
                  </button>
                  <span className="cell-meta">
                    Borrado en cascada de toda la data de la org. R2 objects huérfanos por diseño.
                  </span>
                </div>
              ) : (
                <div>
                  <p style={{ marginTop: 0 }}>
                    Esto elimina <strong>{orgQuery.data.name}</strong> y toda su data (
                    {orgQuery.data.counts.assets} tiendas, {orgQuery.data.counts.reports} reportes,{" "}
                    {orgQuery.data.counts.assetGroups} zonas) en una sola transacción. No hay
                    rollback.
                  </p>
                  <p className="auth-error" role="alert">
                    También se eliminan de forma permanente las{" "}
                    <strong>cuentas de usuario de todos los miembros</strong> de esta organización y
                    sus invitaciones pendientes. Si un miembro pertenece a otra organización,
                    también pierde el acceso a esa. Se eliminan además las cuentas de cualquier
                    correo invitado a esta org que nunca haya aceptado ninguna invitación (sin
                    membresía en ninguna otra org); un invitado que sí es miembro activo de otra org
                    se conserva. Esto es intencional para poder re-testear el flujo de invitación
                    con los mismos correos.
                  </p>
                  {orgQuery.data.counts.signedReports > 0 && (
                    <p className="auth-error" role="alert">
                      Esta organización tiene {orgQuery.data.counts.signedReports} reporte(s)
                      firmado(s) o cerrado(s). El servidor va a rechazar el borrado — los reportes
                      terminales son inmutables (§0.4(1)). Considera <em>soft-disable</em> en su
                      lugar.
                    </p>
                  )}
                  <label
                    htmlFor="org-delete-confirm"
                    className="cell-meta"
                    style={{ display: "block", marginTop: 12, marginBottom: 4 }}
                  >
                    Escribe el nombre exacto de la organización (<code>{orgQuery.data.name}</code>)
                    para confirmar:
                  </label>
                  <input
                    id="org-delete-confirm"
                    type="text"
                    value={dangerTyped}
                    onChange={(e) => setDangerTyped(e.target.value)}
                    placeholder={orgQuery.data.name}
                    disabled={deleteOrg.isPending}
                    className="form-input"
                    style={{ width: "100%", maxWidth: 480 }}
                    autoFocus
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => {
                        setDangerOpen(false);
                        setDangerTyped("");
                        deleteOrg.reset();
                      }}
                      disabled={deleteOrg.isPending}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={deleteOrg.isPending || dangerTyped !== orgQuery.data.name}
                      onClick={() => void handleDeleteConfirm()}
                    >
                      {deleteOrg.isPending ? "Eliminando…" : "Eliminar definitivamente"}
                    </button>
                  </div>
                  {deleteOrg.error && (
                    <div className="auth-error" role="alert" style={{ marginTop: 12 }}>
                      {deleteOrg.error instanceof ApiError
                        ? deleteOrg.error.message
                        : "Falló el borrado. Prueba de nuevo o revisa los logs."}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// The page before the org lands (or when it can't): the real header
// frame — back link, title and subtitle line boxes — with bars where the
// org's name and recipe go, and a body skeleton on the real panels. A
// failure ends in PageError with Reintentar, never a blank page.
function AdminOrgDetailFallback({
  view,
}: {
  view: Exclude<QueryView<unknown>, { kind: "ready" }>;
}) {
  const notFound =
    view.kind === "disabled" ||
    (view.kind === "error" && view.error instanceof ApiError && view.error.status === 404);
  return (
    <div className="page">
      <div className="page-pad">
        <header className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/admin/orgs">← Organizaciones</Link>
            </div>
            <h1 className="page-header__title">
              {view.kind === "loading" ? <Skel variant="text" width={320} /> : "Organización"}
            </h1>
            <p className="page-header__sub">
              {view.kind === "loading" && <Skel variant="text" width={260} />}
            </p>
          </div>
        </header>
        {view.kind === "loading" ? (
          <PageSkeleton label="Cargando la organización…" visible={view.visible}>
            <SkelPanel title="Identificación">
              <dl className="kv-grid">
                {IDENT_LABELS.map((label, i) => (
                  <IdentSkelRow key={label} label={label} width={i === 0 ? "60%" : "35%"} />
                ))}
              </dl>
            </SkelPanel>
            <SkelPanel title="Logo de la organización" />
            <SkelPanel title="Logos por marca" />
          </PageSkeleton>
        ) : notFound ? (
          <PageError
            title="No encontramos la organización"
            description="Puede que se haya eliminado o que el enlace esté mal."
          />
        ) : view.kind === "error" ? (
          <PageError
            title={view.offline ? "Sin conexión" : "No pudimos cargar la organización"}
            description={
              view.offline
                ? "Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                : "Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            }
            onRetry={view.retry}
          />
        ) : null}
      </div>
    </div>
  );
}

function IdentSkelRow({ label, width }: { label: string; width: string }) {
  return (
    <>
      <dt className="cell-meta">{label}</dt>
      <dd>
        <Skel variant="text" width={width} />
      </dd>
    </>
  );
}

// The store picker's silhouette (`.store-picker` fieldset: legend line,
// then the bordered option list) so the real picker replaces it in place.
function StorePickerSkeleton() {
  return (
    <div className="store-picker" aria-hidden="true">
      <div className="store-picker__legend">
        <Skel variant="text" width="30%" />
      </div>
      <div className="store-picker__list">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="store-picker__option">
            <Skel width={14} height={14} />
            <Skel variant="text" width={i % 2 === 0 ? "45%" : "35%"} />
          </div>
        ))}
      </div>
    </div>
  );
}
